//@ts-nocheck

/**
 * Sync And Cache Manager Service
 * Handles caching, syncing, and offline data management for trips
 */
import { pathReference, imageSize } from '../../../constants/files';
import {
  getBucketFileUrl,
  getBucketImageUrl,
  getLineItemAttachmentUrl
} from '../../../utils/resourcePathHelper';

//Define constants
const CACHE_STATUS_KEY_PREFIX = 'trip-cache-status-';
const SYNC_TIMESTAMP_KEY_PREFIX = 'trip-sync-timestamp-';

export class SyncAndCacheManager {
  constructor() {
    this.swRegistration = null;
    this.isOnline = navigator.onLine;
    this.tripDataTransforms = new Map();
    this.setupEventListeners();
    this.apiUrl = null;
  }

  /**
   * Setup online/offline event listeners
   */
  setupEventListeners() {
    window.addEventListener('online', () => {
      this.isOnline = true;
      console.log('[SyncAndCacheManager] Device is online');
      this.syncAllTrips();
    });

    window.addEventListener('offline', () => {
      this.isOnline = false;
      console.log('[SyncAndCacheManager] Device is offline');
    });

    // Listen for service worker messages
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        this.handleServiceWorkerMessage(event.data);
      });
    }
  }

  /**
   * Initialize service worker
   */
  async initServiceWorker() {
    if (!('serviceWorker' in navigator)) {
      console.warn('[SyncAndCacheManager] Service Workers not supported');
      return false;
    }

    try {
      this.swRegistration = await navigator.serviceWorker.register(
        '/service-worker.js',
        {
          scope: '/' // Set scope to allow caching/intercepting /tenant/... attachments and other app resources
        }
      );
      console.log('[SyncAndCacheManager] Service Worker registered');

      // Wait for service worker to be ready
      await navigator.serviceWorker.ready;

      return true;
    } catch (error) {
      console.error(
        '[SyncAndCacheManager] Service Worker registration failed:',
        error
      );
      return false;
    }
  }

  /**
   * Cache trip data for offline access
   */
  async cacheTripData(tripId, tripData) {
    if (!this.swRegistration) {
      await this.initServiceWorker();
    }

    try {
      console.log(`[SyncAndCacheManager] Starting to cache trip ${tripId} `);

      // Update cache status
      this.setCacheStatus(tripId, 'caching');

      // Prepare trip data with all resources
      const preparedData = this.prepareTripDataForCaching(tripData);

      // Send message to service worker to cache the trip
      if (navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({
          type: 'CACHE_TRIP',
          tripId: tripId,
          tripData: preparedData
        });
      }

      // Store in localStorage as backup
      this.storeTripDataLocally(tripId, preparedData);

      return true;
    } catch (error) {
      console.error(
        `[SyncAndCacheManager] Error caching trip ${tripId}:`,
        error
      );
      this.setCacheStatus(tripId, 'error');
      return false;
    }
  }

  getImage(fileName = '', tripid, tenantid, prefix = '') {
    return getBucketImageUrl({
      fileName,
      pathReference: pathReference.DETAILED_DAY_SERVICE,
      tripId: tripid,
      tenantId: tenantid,
      sizePrefix: prefix
    });
  }

  getAttachementUrl(fileName = '', tenantid, tripid) {
    return getBucketFileUrl({
      fileName,
      pathReference: pathReference.DETAILED_DAY_SERVICE,
      tripId: tripid,
      tenantId: tenantid
    });
  }

  getAttachmentsFromService(servicefields = [], tenantid, tripid) {
    const fields = servicefields?.reduce((acc, field) => {
      let values = field.options.map((opt) => opt?.value);

      // Transform 'UPLOADATTACHMENTS' field values to URLs
      if (field?.key === 'UPLOADATTACHMENTS') {
        const attachments = Array.isArray(values)
          ? values
          : values
            ? [values]
            : [];

        const files = field?.files || [];

        values = attachments
          .map((fileName, index) => {
            return {
              url: this.getAttachementUrl(fileName, tenantid, tripid),
              originalName: files?.[index]?.originalname || fileName
            };
          })
          .filter(Boolean);
      }

      if (field?.key === 'UPLOADIMAGES') {
        const imageValues = Array.isArray(values)
          ? values
          : values
            ? [values]
            : [];
        values = {
          thumbnails: imageValues
            .map((value) => {
              return {
                url: this.getImage(
                  value,
                  tripid,
                  tenantid,
                  imageSize.thumbnail.prefix
                ),
                originalName: value
              };
            })
            .filter(Boolean),
          fullSize: imageValues
            .map((value) => {
              return {
                url: this.getImage(
                  value,
                  tripid,
                  tenantid,
                  imageSize.medium.prefix
                ),
                originalName: value
              };
            })
            .filter(Boolean)
        };
      }

      acc[field.key] = values;
      return acc;
    }, {});

    return fields;
  }

  /**
   * Get mapped day service fields (Return only upload images/attachments fields)
   */
  getMappedDayServiceFields(services = []) {
    const servicesList = services?.map((service) => {
      const { servicetype = {}, servicefields = [] } = service;
      const serviceTypeFields = servicetype?.servicetypefield ?? [];
      const mappedServiceFields = servicefields
        ?.map((field) => {
          const foundServiceTypeField = serviceTypeFields.find(
            (item) =>
              item?.servicefield?.servicefieldid === field?.servicefieldid ||
              item?.servicefieldid === field?.servicefieldid
          );

          const key =
            foundServiceTypeField?.key ??
            foundServiceTypeField?.servicefield?.key ??
            '';

          return { ...field, key };
        })
        ?.filter((field) =>
          ['UPLOADIMAGES', 'UPLOADATTACHMENTS'].includes(field?.key)
        );

      return {
        ...service,
        servicefields: mappedServiceFields,
        tripdetaileddayserviceid: service?.tripdetaileddayserviceid
      };
    });

    return servicesList;
  }

  /**
   * Prepare trip data for caching (extract all resources)
   */
  prepareTripDataForCaching(tripData) {
    const {
      tripid,
      tenant,
      tripimage = [],
      tripday = [],
      tripdetailedday = [],
      tripcontent = []
    } = tripData;
    const attachments = [];
    const images = [];

    // Extract attachments from all services
    tripdetailedday?.forEach((day) => {
      const { tripdetaileddayservice = [] } = day;
      const mappedServices = this.getMappedDayServiceFields(
        tripdetaileddayservice
      );

      mappedServices?.forEach((service) => {
        const serviceFields = this.getAttachmentsFromService(
          service.servicefields,
          tenant?.tenantid,
          tripid
        );

        //Push service attachments
        serviceFields?.UPLOADATTACHMENTS?.forEach((attachment) => {
          if (attachment?.url) {
            attachments.push({
              url: attachment.url,
              originalName: attachment?.originalName || 'attachment',
              serviceId: service.tripdetaileddayserviceid
            });
          }
        });

        //Push service images(TH Size)
        serviceFields?.UPLOADIMAGES?.thumbnails?.forEach((img) => {
          if (img?.url) {
            images.push({
              url: img.url,
              optimizedUrl: img?.optimizedUrl || img.url,
              serviceId: service?.tripdetaileddayserviceid
            });
          }
        });

        //Push service images(MD Size)
        serviceFields?.UPLOADIMAGES?.fullSize?.forEach((img) => {
          if (img?.url) {
            images.push({
              url: img?.url,
              optimizedUrl: img?.optimizedUrl || img.url,
              serviceId: service?.tripdetaileddayserviceid
            });
          }
        });
      });
    });

    // Extract image from tripimage
    tripimage?.forEach((image) => {
      if (image?.imageurl) {
        images.push({
          url: image.imageurl,
          optimizedUrl: image.optimizedUrl || image.imageurl,
          tripImageId: image.tripimageid || null
        });
      }
    });

    // Extract attachments from new lineitem shape (lineitemattachment)
    tripday?.forEach((day) => {
      day?.lineitem?.forEach((item) => {
        item?.lineitemattachment?.forEach((att) => {
          if (att?.resourcepath) {
            const url = getLineItemAttachmentUrl({
              resourcePath: att.resourcepath
            });
            if (url) {
              attachments.push({
                url,
                originalName: att.attachmentname || 'attachment',
                lineItemId: item.lineitemid
              });
            }
          }
        });
      });
    });

    // Extract images from all days
    tripday?.forEach((day) => {
      day?.tripdayimage?.forEach((image) => {
        if (image?.imageurl) {
          images.push({
            url: image.imageurl,
            optimizedUrl: image.optimizedUrl || image.imageurl,
            dayId: day.tripdayid
          });
        }
      });
    });

    // Extract tenant logo
    if (tenant?.tenantlogo) {
      images.push({
        url: tenant.tenantlogo,
        optimizedUrl: tenant.tenantlogo,
        tenantId: tenant?.tenantid
      });
    }

    //Extract trip content images
    tripcontent?.forEach((content) => {
      const { tripcontentimage = [] } = content;
      tripcontentimage?.forEach((image) => {
        if (image?.imageurl) {
          images.push({
            url: image.imageurl,
            optimizedUrl: image.optimizedUrl || image.imageurl,
            contentId: content.tripcontentid
          });
        }
      });
    });

    return {
      ...tripData,
      attachments,
      images
    };
  }

  /**
   * Store trip data in localStorage as backup
   */
  storeTripDataLocally(tripId, tripData) {
    try {
      const key = `${tripId}-TripData`;
      // Store only essential data (not attachments/images to save space)
      const essentialData = {
        ...tripData,
        attachments: undefined,
        images: undefined
      };
      localStorage.setItem(key, JSON.stringify(essentialData));
      localStorage.setItem(
        SYNC_TIMESTAMP_KEY_PREFIX + tripId,
        Date.now().toString()
      );
    } catch (error) {
      console.error(
        '[SyncAndCacheManager] Error storing data in localStorage:',
        error
      );
    }
  }

  /**
   * Get cached trip data from localStorage
   */
  getCachedTripData(tripId) {
    try {
      const key = `${tripId}-TripData`;
      const data = localStorage.getItem(key);
      return data ? JSON.parse(data) : null;
    } catch (error) {
      console.error(
        '[SyncAndCacheManager] Error retrieving cached data:',
        error
      );
      return null;
    }
  }

  /**
   * Check if trip data is cached
   */
  isTripCached(tripId) {
    const status = this.getCacheStatus(tripId);
    return status === 'cached' || status === 'synced';
  }

  /**
   * Get cache status for a trip
   */
  getCacheStatus(tripId) {
    return (
      localStorage.getItem(CACHE_STATUS_KEY_PREFIX + tripId) || 'not-cached'
    );
  }

  /**
   * Set cache status for a trip
   */
  setCacheStatus(tripId, status) {
    localStorage.setItem(CACHE_STATUS_KEY_PREFIX + tripId, status);
  }

  /**
   * Register a trip data transform used before syncing/caching a trip.
   */
  setTripDataTransform(tripId, transformTripData = null) {
    const transformKey = String(tripId);

    if (typeof transformTripData === 'function') {
      this.tripDataTransforms.set(transformKey, transformTripData);
      return;
    }

    this.tripDataTransforms.delete(transformKey);
  }

  /**
   * Handle messages from service worker
   */
  handleServiceWorkerMessage(data) {
    if (data.type === 'CACHE_COMPLETE') {
      console.log(
        `[SyncAndCacheManager] Cache complete for trip ${data.tripId}`
      );
      this.setCacheStatus(data.tripId, 'cached');

      // Dispatch custom event
      window.dispatchEvent(
        new CustomEvent('tripCacheComplete', {
          detail: { tripId: data.tripId }
        })
      );
    }

    if (data.type === 'CACHE_ERROR') {
      console.error(
        `[SyncAndCacheManager] Cache error for trip ${data.tripId}:`,
        data.error
      );
      this.setCacheStatus(data.tripId, 'error');

      // Dispatch custom event
      window.dispatchEvent(
        new CustomEvent('tripCacheError', {
          detail: { tripId: data.tripId, error: data.error }
        })
      );
    }
  }

  /**
   * Sync trip data when online
   */
  async syncTripData(tripId, tripKey, { transformTripData } = {}) {
    if (!this.isOnline) {
      console.log('[SyncAndCacheManager] Device offline, skipping sync');
      return false;
    }

    try {
      console.log(`[SyncAndCacheManager] Syncing trip ${tripId}`);

      // Fetch fresh data from API
      const response = await fetch(
        `${this.apiUrl}trips/${tripId}/app?tripKey=${tripKey}`
      );

      if (!response?.ok) {
        throw new Error('Failed to fetch trip data');
      }

      const freshData = await response.json();
      const tripDataTransform =
        transformTripData || this.tripDataTransforms.get(String(tripId));
      const nextTripData =
        typeof tripDataTransform === 'function'
          ? tripDataTransform(freshData?.data)
          : freshData?.data;

      // Check if data has changed
      const cachedData = this.getCachedTripData(tripId);
      const hasChanged = this.hasDataChanged(cachedData, nextTripData);

      if (hasChanged) {
        console.log(
          `[SyncAndCacheManager] Trip ${tripId} has updates, re-caching`
        );

        await this.cacheTripData(tripId, nextTripData);
        this.setCacheStatus(tripId, 'synced');

        // Dispatch update event
        window.dispatchEvent(
          new CustomEvent('tripDataUpdated', {
            detail: { tripId, data: nextTripData, rawData: freshData.data }
          })
        );
      } else {
        console.log(`[SyncAndCacheManager] Trip ${tripId} is up to date`);
        this.setCacheStatus(tripId, 'synced');
      }

      return true;
    } catch (error) {
      console.error(
        `[SyncAndCacheManager] Error syncing trip ${tripId}:`,
        error
      );
      return false;
    }
  }

  /**
   * Check if trip data has changed
   */
  hasDataChanged(oldData, newData) {
    if (!oldData || !newData) return true;

    // Simple comparison of JSON strings
    // In production, you might want more sophisticated comparison
    return JSON.stringify(oldData) !== JSON.stringify(newData);
  }

  /**
   * Sync all cached trips
   */
  async syncAllTrips() {
    const tripIds = this.getAllCachedTripIds();

    for (const tripId of tripIds) {
      // Get trip key from URL or storage
      const tripKey = new URLSearchParams(window.location.search).get('uuid');
      if (tripKey) {
        await this.syncTripData(tripId, tripKey);
      }
    }
  }

  /**
   * Get all cached trip IDs
   */
  getAllCachedTripIds() {
    const tripIds = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.endsWith('-TripData')) {
        const tripId = key.replace('-TripData', '');
        tripIds.push(tripId);
      }
    }

    return tripIds;
  }

  /**
   * Clear cache for a trip
   */
  async clearTripCache(tripId) {
    try {
      console.log(`[SyncAndCacheManager] Clearing cache for trip ${tripId}`);

      // Clear localStorage
      localStorage.removeItem(`${tripId}-TripData`);
      localStorage.removeItem(CACHE_STATUS_KEY_PREFIX + tripId);
      localStorage.removeItem(SYNC_TIMESTAMP_KEY_PREFIX + tripId);

      // Clear service worker cache
      if (navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({
          type: 'CLEAR_TRIP_CACHE',
          tripId: tripId
        });
      }

      return true;
    } catch (error) {
      console.error(
        `[SyncAndCacheManager] Error clearing cache for trip ${tripId}:`,
        error
      );
      return false;
    }
  }

  /**
   * Get storage usage estimate
   */
  async getStorageEstimate() {
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      try {
        const estimate = await navigator.storage.estimate();
        return {
          usage: estimate.usage,
          quota: estimate.quota,
          usagePercent: (estimate.usage / estimate.quota) * 100
        };
      } catch (error) {
        console.error(
          '[SyncAndCacheManager] Error getting storage estimate:',
          error
        );
        return null;
      }
    }
    return null;
  }

  /**
   * Check if there's enough storage space
   */
  async hasEnoughStorage(requiredBytes = 50 * 1024 * 1024) {
    const estimate = await this.getStorageEstimate();

    if (!estimate) return true; // Assume yes if we can't check

    const availableSpace = estimate?.quota - estimate?.usage;
    return availableSpace >= requiredBytes;
  }
}

// Export singleton instance
export const syncAndCacheManager = new SyncAndCacheManager();
